/**
 * AccountWebsitesDrawer — Accounts S1 (spec §3.2 "Find websites", §3.4 "Add
 * website"; RULED 2026-10-24): the account edit form, filtered to the
 * accounts that have no website. "Find websites" opens it on every such
 * account; a row's "Add website" opens it on that one account. No external
 * lookup — the user types the address. Each save is the existing
 * PATCH /v1/sales/accounts/{id} with ONLY the website field (an omitted field
 * keeps its value), so the account's history records it like any other edit.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Globe } from 'lucide-react'
import { toast } from 'sonner'
import { updateAccount } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { overlay } from '../../../../theme/tokens'
import { Avatar } from '../../DashboardShared'

export interface WebsiteTarget { id: string; name: string }

export function AccountWebsitesDrawer({ accounts, onClose, onSaved }: {
  /** The accounts the form is filtered to — every one has no website. */
  accounts: WebsiteTarget[]
  onClose: () => void
  onSaved: (id: string, website: string) => void
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [saved, setSaved]   = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)

  async function save(a: WebsiteTarget) {
    const website = (drafts[a.id] ?? '').trim()
    if (!website) { toast.error(`Add a website for ${a.name} first`); return }
    setSaving(a.id)
    try {
      const next = await updateAccount(a.id, { website })
      setSaved(prev => ({ ...prev, [a.id]: next.website }))
      onSaved(a.id, next.website)
      toast.success(`Website saved for ${next.name}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update account'))
    } finally {
      setSaving(null)
    }
  }

  const inputCls = 'flex-1 min-w-0 h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer" role="dialog" aria-label="Accounts without a website"
        style={{ width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Accounts without a website</div>
            <div className="text-12 text-ink-muted mt-1">
              {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col">
          {accounts.length === 0 && <p className="text-13 text-ink-muted py-4">Every account has a website.</p>}
          {accounts.map(a => (
            <div key={a.id} className="flex items-center gap-3 py-3 border-b border-edge-row last:border-b-0">
              <Avatar name={a.name} size={28} />
              <div className="w-40 min-w-0 text-13 font-semibold text-ink-primary truncate">{a.name}</div>
              {saved[a.id] !== undefined ? (
                <span className="flex-1 min-w-0 inline-flex items-center gap-1 text-12 text-ink-secondary truncate">
                  <Globe size={12} className="text-ink-faint" />{saved[a.id]}
                </span>
              ) : (
                <>
                  <input value={drafts[a.id] ?? ''} onChange={e => setDrafts(prev => ({ ...prev, [a.id]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') void save(a) }}
                    placeholder="https://example.com" aria-label={`Website for ${a.name}`}
                    autoFocus={accounts.length === 1} className={inputCls} />
                  <button className="k-btn disabled:opacity-50" disabled={saving !== null} onClick={() => void save(a)}>
                    {saving === a.id ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">Done</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
